import { validTextLength } from '@/environment';
import { getPageProperty, PolotnoStoreActivePageKeys } from '@/pages/AdvancedEditor/hooks';
import { AudioData } from '@/pages/AdvancedEditor/hooks/useUploadAudio';
import { getAvatarElementFromPage, getAvatarPresenterFromPage } from '@/pages/AdvancedEditor/utils';
import { IPresenter, IVoice, ScriptType } from '@d-id/types';
import { ElementType } from 'polotno/model/group-model';
import { StoreType } from 'polotno/model/store';

export const validatePage = ({
    selectedScript,
    text,
    selectedVoice,
    uploadedAudio,
    presenter,
    avatarElement,
    sceneNumber,
}: {
    selectedScript: ScriptType;
    text: string;
    selectedVoice: IVoice | null;
    uploadedAudio: AudioData | null;
    presenter?: IPresenter;
    avatarElement?: ElementType;
    sceneNumber: number;
}) => {
    // write the scene as 1st 2nd 3rd 4th etc
    const sceneNumberString = `${sceneNumber + 1}${sceneNumber === 0 ? 'st' : sceneNumber === 1 ? 'nd' : sceneNumber === 2 ? 'rd' : 'th'}`;
    if (!presenter || !avatarElement) {
        return `Your ${sceneNumberString} scene is missing an Avatar.\nPlease add an Avatar to generate your video.`;
    }
    if ((presenter.faces?.length ?? 0) === 0 && !presenter.isDefault) {
        return 'Image has no faces detected';
    }
    if (selectedScript === ScriptType.text) {
        if (!selectedVoice) {
            return 'Please select a voice before generating a video';
        }
        if (!text.length) {
            return `Your ${sceneNumberString} scene is missing a script.\nPlease add a script to generate your video.`;
        }
        if (text.length < validTextLength) {
            return `Your ${sceneNumberString} scene has less than 5 characters.\nPlease add more characters to generate your video.`;
        }
    }
    if (selectedScript === ScriptType.audio && !uploadedAudio) {
        return `Your ${sceneNumberString} scene is missing a script.\nPlease add a script to generate your video.`;
    }

    return undefined;
};

export const getGenerationValidationError = (store: StoreType) => {
    return store.pages
        .map((page, index) => {
            const avatarElement = getAvatarElementFromPage(page);
            const presenter = getAvatarPresenterFromPage(page);

            const selectedScript = getPageProperty(page, PolotnoStoreActivePageKeys.selectedScript);
            const text = getPageProperty(page, PolotnoStoreActivePageKeys.textInput).trim();
            const selectedVoice = getPageProperty(page, PolotnoStoreActivePageKeys.ttsSelectedVoice);
            const uploadedAudio = getPageProperty(page, PolotnoStoreActivePageKeys.uploadedAudio);

            return validatePage({
                selectedScript,
                text,
                selectedVoice,
                uploadedAudio,
                presenter,
                avatarElement,
                sceneNumber: index,
            });
        })
        .filter(error => !!error)[0];
};
